import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { StateInterface } from 'client/Interfaces/StateInterface'
import { productsSelector } from 'client/reducers/products'
import { Dispatch } from 'redux'

import * as apiV4 from '../api/productsV4'
import { Analytics, checkPlacement } from '../Global/Analytics'
import { AnalyticsType, Category, Events, Placement, Result } from '../Global/AnalyticsEvents'
import { ProductInterface, ProductTypesEnum } from '../Interfaces/ProductInterface'
import { reCalculatePreciseUnitPrice } from '../Other/functions/invoiceLineFunctions'
import { Immutable } from '../Other/Immutable'
import { ProductActions } from './actionTypes'
import { actionCreatorAsync, AsyncAction } from './index'

export function fetchProducts() {
  return (dispatch: Dispatch, getState: () => StateInterface): void => {
    const products = productsSelector(getState())
    if (products !== null && products.length > 0) {
      return
    }
    dispatch(getAllProducts())
  }
}

export const putProductToLine = (state, product: ProductInterface, externalTaxRate?) => {
  // checking both values (id and pct), as we had cases when we had the id, but
  // not the percentage and it resulted in weird/misleading behavior in the UI
  const isExternalVatRate = !!externalTaxRate?.vatRateId && !!externalTaxRate?.vatPct
  const isProductVatRate = !!product?.vatRateId && !!product?.vatPct
  let vatRateId = product?.vatRateId
  let vatPct = product?.vatPct

  if (!isProductVatRate && isExternalVatRate) {
    vatRateId = externalTaxRate?.vatRateId
    vatPct = externalTaxRate?.vatPct
  }

  return reCalculatePreciseUnitPrice({
    ...state,
    defField: product?.pricingModel || '',
    productType: product?.productType,
    grossTotal: product?.total,
    unit: product?.unit,
    description: product?.name,
    unitPrice: product?.price,
    // for next users: fall back to blank for product without vat rate id
    // vatRateId: (externalTaxRate && !product.vatRateId) ? null : product?.vatRateId,
    vatRateId: vatRateId || null,
    vatPct: vatPct || 0,
  })
}

// new Product API
export function getAllProducts(): AsyncAction<ProductInterface[]> {
  return actionCreatorAsync<ProductInterface[]>(ProductActions.LOAD_PRODUCTS, async () => {
    return await apiV4.getProducts()
  })
}

export function getProduct(id: number): AsyncAction<ProductInterface> {
  return actionCreatorAsync<ProductInterface>(ProductActions.GET_PRODUCT, async () => {
    return await apiV4.getProduct(id)
  })
}

function handleProductBeforeSave(product: ProductInterface): ProductInterface {
  const newProduct = Immutable.copy(product)
  if (!newProduct?.total) {
    newProduct.total = 0
  }
  newProduct.description = newProduct?.description !== '' ? newProduct?.description : null
  return newProduct
}

export function addProduct(product: ProductInterface, context?: string): AsyncAction<ProductInterface> {
  return actionCreatorAsync<ProductInterface>(ProductActions.CREATE_PRODUCT, async () => {
    const newProduct = await apiV4.addProduct(handleProductBeforeSave(product))
    Analytics.trackEvent(Events.addProduct, {
      type: AnalyticsType.action,
      category: Category.product,
      result: Result.success,
      placement: checkPlacement(window.location.pathname),
      productId: newProduct?.id,
    })

    Segment.track(SegmentEvents.product.add, { context: context || 'product' })

    return newProduct
  })
}

export function updateProduct(product: ProductInterface): AsyncAction<ProductInterface> {
  return actionCreatorAsync<ProductInterface>(ProductActions.UPDATE_PRODUCT, async () => {
    Analytics.trackEvent(Events.editProduct, {
      type: AnalyticsType.action,
      category: Category.product,
      result: Result.success,
      placement: checkPlacement(window.location.pathname),
      productId: product?.id,
    })

    Segment.track(SegmentEvents.product.edit)

    return await apiV4.updateProduct(handleProductBeforeSave(product))
  })
}

export function deleteProduct(id: number): AsyncAction<number> {
  return actionCreatorAsync<number>(ProductActions.DELETE_PRODUCT, async () => apiV4.deleteProduct(id))
}

export function deleteProducts(ids: number[]): AsyncAction<number[]> {
  return actionCreatorAsync<number[]>(ProductActions.DELETE_PRODUCTS, async () => apiV4.deleteProducts(ids))
}

export function updateProducts(ids: number[], change: Partial<ProductInterface>): AsyncAction<ProductInterface[]> {
  return actionCreatorAsync<ProductInterface[]>(ProductActions.UPDATE_PRODUCTS, async () => {
    return apiV4.updateProducts(ids, change)
  })
}
